/**
 * "Preview" for Admin → Announcements: the announcement exactly as people
 * will see it — the real popup or the real banner, not a mock-up — without
 * publishing it or recording anyone as having seen it. The popup's button
 * doesn't navigate in preview; every way out just closes.
 */

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, Dialog, IconButton, Typography } from '@mui/material';

import type { Announcement } from '../../api/announcements';
import { AnnouncementBanner } from '../../components/announcements/AnnouncementBanner';
import { AnnouncementModal } from '../../components/announcements/AnnouncementModal';
import { colors, uiType } from '../../theme';

export function AnnouncementPreview({
  announcement,
  onClose,
}: {
  announcement: Announcement | null;
  onClose: () => void;
}) {
  if (!announcement) return null;

  if (announcement.presentation === 'modal') {
    return (
      <AnnouncementModal
        announcement={announcement}
        preview
        onDismiss={onClose}
        index={1}
        total={1}
        onNext={onClose}
        onBack={onClose}
        canGoBack={false}
      />
    );
  }

  // A banner lives under the page header, so show it on a sketch of one.
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth aria-label="Banner preview">
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25 }}>
        <Typography
          sx={{
            flex: 1,
            fontSize: uiType.caption,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: colors.textMuted,
          }}
        >
          Banner preview — shown under the header on every page
        </Typography>
        <IconButton size="small" aria-label="Close preview" onClick={onClose}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box
        sx={{
          mx: 2,
          mb: 2,
          border: `1px solid ${colors.borderLight}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: 44,
            bgcolor: colors.brandCream,
            borderBottom: `1px solid ${colors.borderLight}`,
          }}
        />
        <AnnouncementBanner announcement={announcement} onDismiss={onClose} />
        <Box sx={{ p: 2, display: 'grid', gap: 1 }}>
          {[70, 92, 55].map((w) => (
            <Box
              key={w}
              sx={{ height: 8, width: `${w}%`, borderRadius: 4, bgcolor: 'rgba(15,10,7,0.06)' }}
            />
          ))}
        </Box>
      </Box>
    </Dialog>
  );
}
